import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { isServiceRateForRoom } from 'lib/offer/offerPageRoomRate'
import { useContext, useEffect, useMemo } from 'react'
import { arePropertyRatesLoading } from 'selectors/offerPage/offerPageSelectors'
import { getValidRatesByRoom } from 'selectors/offerPage/propertyMultipleRoomBookingSelectors'

import useOfferPageDispatch from './useOfferPageDispatch'

/**
 * Swaps reservation/bedbank service ids (from cart items or deeplinks) for the matching rate's
 * option ids, so the multi-room selectors only ever compare option ids. Single-room searches have
 * no per-room rates, so their ids are left as they are. Returns true while a swap is pending, so
 * autoselection can wait for it rather than discard the selection.
 */
export default function usePropertyServiceRoomIdSwap(): boolean {
  const offerPageState = useContext(OfferPageStateContext)
  const { rooms } = offerPageState
  const dispatch = useOfferPageDispatch()

  const isRatesLoading = useAppSelector(appState => arePropertyRatesLoading(offerPageState, appState))
  const validRatesByRoom = useAppSelector(appState => getValidRatesByRoom(offerPageState, appState))

  const roomsWithOptionIds = useMemo(() => {
    if (isRatesLoading) {
      return rooms
    }
    const swappedRooms = rooms.map((room) => {
      const serviceRate = (validRatesByRoom[room.id] ?? EmptyArray).find(rate => isServiceRateForRoom(rate, room))
      return serviceRate ? { ...room, roomId: serviceRate.roomId, rateId: serviceRate.optionId } : room
    })
    return swappedRooms.some((room, index) => room !== rooms[index]) ? swappedRooms : rooms
  }, [isRatesLoading, rooms, validRatesByRoom])

  useEffect(() => {
    if (roomsWithOptionIds !== rooms) {
      dispatch({ type: OfferPageActions.setRooms, rooms: roomsWithOptionIds })
    }
  }, [dispatch, rooms, roomsWithOptionIds])

  return roomsWithOptionIds !== rooms
}
